'use client';

import Accordion from '@ui/components/Product/components/Accordion';
import { Fragment, useMemo } from 'react';
import type { Dictionary } from '@ts/dictionary';
import type { LoopFetchProductResult } from '@ts/loopFetchProductResult';
import type { Variant } from '@ts/variant';
import { cn } from '@ngg/storefront-utils';
import type { Maybe, SlugPageFragment } from '@business/gql/graphql';
import Link from '@ui/components/Link/Link';
import config from '@utils/config';
import {
  properties as propertyList,
  propertySuffixMap,
} from '@utils/product/properties';
import Paragraph from '@ui/components/Paragraph/Paragraph';

type AttributeValue = string | number | [] | null;

const splitArray = (value: AttributeValue) => {
  if (typeof value === 'string' && value.includes(',')) {
    return value.split(',');
  }
};

const renderAttrValueWithLinks = ({
  value,
  links,
  commerceIds,
}: {
  value: AttributeValue;
  links: Maybe<SlugPageFragment>[];
  commerceIds: string | string[];
}) => {
  const { locale } = config;
  const values = splitArray(value) ?? [value];

  if (values) {
    return values.map((value, index) => {
      let link;

      if (values.length === 1) {
        link =
          links.find((link) => link?.title === value) ?? // First try to find by title
          links.find((link) => commerceIds.includes(link?.commerceId ?? '')); // This covers the case when we can't match value name with link title
      } else {
        link = links.find((link) => link?.title === value); // We should only identify the link by title when we have multiple values
      }

      return link?.slug ? (
        <Fragment key={index + '_' + value?.toString()}>
          <Link
            href={link?.slug}
            locale={locale}
            className="lg:hover:underline">
            {value}
          </Link>
          {index !== values.length - 1 ? ', ' : ''}
        </Fragment>
      ) : (
        <Fragment key={index + '_' + value?.toString()}>
          <span>{value}</span>
          {index !== values.length - 1 ? ', ' : ''}
        </Fragment>
      );
    });
  }

  return '-';
};

const getAttributeWithSuffix = ({
  property,
  attribute,
}: {
  property: string;
  attribute: string;
}) => {
  if (propertySuffixMap[property]) {
    return `${attribute} ${propertySuffixMap[property]}`;
  }

  return attribute;
};

const ProductAttributesList = ({
  entity,
  dictionary,
  slugs,
}: {
  entity: Record<string, any>;
  dictionary: Dictionary;
  slugs: Maybe<SlugPageFragment>[];
}) => {
  return (
    <div className="grid grid-cols-[max-content,_auto] gap-x-4 gap-y-2 text-sm">
      {propertyList.map((property) => {
        if (property === 'norce_brand' && entity[property]) {
          const brandId = entity['Brands.Code'];
          if (!brandId?.length) return null;
          const commerceIds = brandId.length <= 1 ? [brandId] : brandId;

          return (
            <Fragment key={property}>
              <Paragraph className="font-sans font-bold">
                {dictionary[property] ?? property}:
              </Paragraph>
              <Paragraph>
                {renderAttrValueWithLinks({
                  value: entity[property],
                  links: slugs,
                  commerceIds,
                })}
              </Paragraph>
            </Fragment>
          );
        }
        if (property === 'norce_designer' && entity[property]) {
          const designerId = entity['Designers.Code'];
          if (!designerId?.length) return null;
          const commerceIds =
            designerId.length <= 1 ? [designerId] : designerId;

          return (
            <Fragment key={property}>
              <Paragraph className="font-sans font-bold">
                {dictionary[property] ?? property}:
              </Paragraph>
              <Paragraph>
                {renderAttrValueWithLinks({
                  value: entity[property],
                  links: slugs,
                  commerceIds,
                })}
              </Paragraph>
            </Fragment>
          );
        }
        if (entity[property] && !Array.isArray(entity[property])) {
          const attribute = entity[property];
          const dictKey = property === 'id' ? 'SKU' : property;
          return (
            <Fragment key={property}>
              <Paragraph className="font-sans font-bold">
                {dictionary[dictKey] ?? property}:
              </Paragraph>
              <Paragraph>
                {getAttributeWithSuffix({
                  attribute,
                  property,
                })}
              </Paragraph>
            </Fragment>
          );
        }
      })}
    </div>
  );
};

export default function Properties({
  dictionary,
  variant,
  product,
  slugs,
  className,
}: {
  dictionary: Dictionary;
  variant?: Variant;
  product: LoopFetchProductResult['product'];
  slugs: Maybe<SlugPageFragment>[];
  className?: string;
}) {
  const entity = useMemo(
    () => ({ ...product, ...variant }),
    [product, variant],
  );

  return (
    <Accordion
      title={dictionary.productInformation}
      WrapperComponent="div"
      transitionProps={{ appear: false }}
      classNames={{
        wrapper: className,
        button: 'text-sm',
      }}
      defaultOpen>
      <ProductAttributesList
        entity={entity}
        dictionary={dictionary}
        slugs={slugs}
      />
      <div className={cn('bg-beige md:hidden', 'mt-4 p-4')}>
        <Paragraph className={cn('text-grey-300 text-xs')}>
          {dictionary.imageDisclaimerText}
        </Paragraph>
      </div>
    </Accordion>
  );
}
